import { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const NOTES = {
  users: "This removes the account, its sessions, connected-shop tokens, listings, fixes and history, and frees any real-shop spot.",
  waitlist: "Only the waitlist entry is removed.",
  invites: "The invitee's account (if any) is kept; a connected real shop is disconnected.",
  feedback: "",
};

// Selection + confirm-delete state shared by the Admin lists.
export const useAdminDelete = (kind, onDone) => {
  const [selected, setSelected] = useState(new Set());
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (ids) => setSelected((s) => (ids.every((i) => s.has(i)) ? new Set() : new Set(ids)));
  const ask = (ids, warn = "") => setPending({ ids, warn });
  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/delete", { kind, ids: pending.ids });
      toast.success(`${data.deleted} ${data.deleted === 1 ? "entry" : "entries"} deleted.${data.accounts_kept?.length ? " Their accounts were kept." : ""}`);
      setSelected(new Set());
      setPending(null);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return { kind, selected, toggle, toggleAll, ask, pending, setPending, confirm, busy };
};

export const SelectBox = ({ d, id, label, m }) => (
  <Checkbox checked={d.selected.has(id)} onCheckedChange={() => d.toggle(id)} aria-label={label || "Select row"}
    data-testid={`admin-${d.kind}-select-${id}${m ? "-mobile" : ""}`} className="h-5 w-5" />
);

export const SelectAll = ({ d, ids, m }) => (
  <Checkbox checked={ids.length > 0 && ids.every((i) => d.selected.has(i))} onCheckedChange={() => d.toggleAll(ids)}
    aria-label="Select all" data-testid={`admin-${d.kind}-select-all${m ? "-mobile" : ""}`} className="h-5 w-5" />
);

export const RowDelete = ({ d, id, warn, disabled, m }) => (
  <Button size="icon" variant="ghost" disabled={disabled} onClick={() => d.ask([id], warn)} aria-label="Delete"
    data-testid={`admin-${d.kind}-delete-${id}${m ? "-mobile" : ""}`} className="h-11 w-11 text-muted-foreground hover:text-rose-300"><Trash2 className="h-4 w-4" /></Button>
);

export const DeleteBar = ({ d, warnFor }) => d.selected.size > 0 && (
  <div data-testid={`admin-${d.kind}-delete-bar`} className="mb-3 flex flex-wrap items-center gap-3 rounded-2xl border border-rose-400/30 bg-rose-400/[0.06] px-4 py-2.5 text-sm">
    <span>{d.selected.size} selected</span>
    <Button size="sm" variant="outline" className="rounded-full border-rose-400/40 text-rose-200" data-testid={`admin-${d.kind}-delete-selected`}
      onClick={() => d.ask([...d.selected], warnFor?.([...d.selected]) || "")}>
      <Trash2 className="mr-1 h-3.5 w-3.5" />Delete selected
    </Button>
    <Button size="sm" variant="ghost" className="rounded-full" onClick={() => d.toggleAll([])} data-testid={`admin-${d.kind}-clear-selection`}>Clear</Button>
  </div>
);

export const ConfirmDelete = ({ d }) => {
  const n = d.pending?.ids.length || 0;
  return (
    <AlertDialog open={!!d.pending} onOpenChange={(o) => !o && d.setPending(null)}>
      <AlertDialogContent data-testid="admin-delete-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="admin-delete-dialog-title">Delete {n} {n === 1 ? "entry" : "entries"}? This can't be undone.</AlertDialogTitle>
          <AlertDialogDescription data-testid="admin-delete-dialog-note">
            {NOTES[d.kind]}{d.pending?.warn && <span className="mt-2 block text-[#F3DDAE]" data-testid="admin-delete-dialog-warn">{d.pending.warn}</span>}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="admin-delete-confirm" disabled={d.busy} onClick={(e) => { e.preventDefault(); d.confirm(); }}
            className="bg-rose-500 text-white hover:bg-rose-500/90">{d.busy ? "Deleting…" : "Delete"}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
